import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for debugging
api.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      const url = error.config?.url || '';
      const isBackgroundCall = url.includes('/settings') || url.includes('/branding');
      if (!isBackgroundCall && path !== '/login' && path !== '/demo' && !path.startsWith('/invite/') && !path.startsWith('/website')) {
        window.location.href = '/demo?tab=signin';
      }
    }
    // Demo mode read-only toast
    if (error.response?.status === 403 && error.response?.data?.detail?.includes('Demo mode')) {
      window.dispatchEvent(new CustomEvent('demo-readonly', { detail: error.response.data.detail }));
    }
    return Promise.reject(error);
  }
);

// User APIs
export const userApi = {
  list: () => api.get('/api/users'),
  get: (id) => api.get(`/api/users/${id}`),
  create: (data) => api.post('/api/users', data),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  delete: (id) => api.delete(`/api/users/${id}`),
  getRoles: () => api.get('/api/users/roles'),
  changePassword: (data) => api.post('/api/users/change-password', data),
  resetPassword: (id, data) => api.post(`/api/users/${id}/reset-password`, data),
  blockUser: (id) => api.post(`/api/users/${id}/block`),
  unblockUser: (id) => api.post(`/api/users/${id}/unblock`),
};

// Project APIs
export const projectApi = {
  list: () => api.get('/api/projects'),
  get: (id) => api.get(`/api/projects/${id}`),
  create: (data) => api.post('/api/projects', data),
  update: (id, data) => api.put(`/api/projects/${id}`, data),
  delete: (id) => api.delete(`/api/projects/${id}`),
};

// Team APIs
export const teamApi = {
  list: (projectId) => api.get('/api/teams', { params: { project_id: projectId } }),
  get: (id) => api.get(`/api/teams/${id}`),
  create: (data) => api.post('/api/teams', data),
  update: (id, data) => api.put(`/api/teams/${id}`, data),
  delete: (id) => api.delete(`/api/teams/${id}`),
  addMember: (teamId, memberId) => api.post(`/api/teams/${teamId}/members`, { member_id: memberId }),
  removeMember: (teamId, memberId) => api.delete(`/api/teams/${teamId}/members/${memberId}`),
  getCategories: () => api.get('/api/teams/categories'),
};

// WBS APIs
export const wbsApi = {
  listActivities: (trade, subcategory) => api.get('/api/wbs/activities', { params: { trade, subcategory } }),
  getTrades: () => api.get('/api/wbs/trades'),
  getSubcategories: (trade) => api.get(`/api/wbs/subcategories/${trade}`),
  createActivity: (data) => api.post('/api/wbs/activities', data),
  updateActivity: (id, data) => api.put(`/api/wbs/activities/${id}`, data),
  getProjectActivities: (projectId) => api.get(`/api/wbs/project/${projectId}/activities`),
  linkActivity: (projectId, data) => api.post(`/api/wbs/project/${projectId}/activities`, data),
  unlinkActivity: (projectId, linkId) => api.delete(`/api/wbs/project/${projectId}/activities/${linkId}`),
};

// Milestone APIs
export const milestoneApi = {
  list: (projectId) => api.get('/api/milestones', { params: { project_id: projectId } }),
  get: (id) => api.get(`/api/milestones/${id}`),
  create: (data) => api.post('/api/milestones', data),
  update: (id, data) => api.put(`/api/milestones/${id}`, data),
  delete: (id) => api.delete(`/api/milestones/${id}`),
};

// Task APIs
export const taskApi = {
  list: (milestoneId, assignedTo) => api.get('/api/tasks', { params: { milestone_id: milestoneId, assigned_to: assignedTo } }),
  get: (id) => api.get(`/api/tasks/${id}`),
  create: (data) => api.post('/api/tasks', data),
  update: (id, data) => api.put(`/api/tasks/${id}`, data),
  delete: (id) => api.delete(`/api/tasks/${id}`),
  createSubtask: (taskId, data) => api.post(`/api/tasks/${taskId}/subtasks`, data),
  updateSubtask: (taskId, subtaskId, data) => api.put(`/api/tasks/${taskId}/subtasks/${subtaskId}`, data),
  deleteSubtask: (taskId, subtaskId) => api.delete(`/api/tasks/${taskId}/subtasks/${subtaskId}`),
};

// Dashboard APIs
export const dashboardApi = {
  getStats: () => api.get('/api/dashboard/stats'),
};

// ==================== PHASE B APIs ====================

// Daily Logs
export const dailyLogApi = {
  list: (params) => api.get('/api/daily-logs', { params }),
  create: (data) => api.post('/api/daily-logs', data),
  update: (id, data) => api.put(`/api/daily-logs/${id}`, data),
  delete: (id) => api.delete(`/api/daily-logs/${id}`),
  listAttachments: (logId) => api.get(`/api/daily-logs/${logId}/attachments`),
  uploadAttachment: (logId, formData) => api.post(`/api/daily-logs/${logId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAttachment: (logId, fileId) => api.delete(`/api/daily-logs/${logId}/attachments/${fileId}`),
  getFileUrl: (filename) => `${API_URL}/api/daily-logs/attachments/file/${filename}`,
};

// Manpower Logs
export const manpowerApi = {
  list: (params) => api.get('/api/manpower', { params }),
  create: (data) => api.post('/api/manpower', data),
  update: (id, data) => api.put(`/api/manpower/${id}`, data),
  delete: (id) => api.delete(`/api/manpower/${id}`),
};

// Material Usage
export const materialApi = {
  list: (params) => api.get('/api/materials', { params }),
  summary: (projectId) => api.get('/api/materials/summary', { params: { project_id: projectId } }),
  create: (data) => api.post('/api/materials', data),
  update: (id, data) => api.put(`/api/materials/${id}`, data),
  delete: (id) => api.delete(`/api/materials/${id}`),
};

// Team Reports
export const teamReportApi = {
  list: (params) => api.get('/api/team-reports', { params }),
  checkSubmission: (teamId, date) => api.get('/api/team-reports/check-submission', { params: { team_id: teamId, report_date: date } }),
  create: (data) => api.post('/api/team-reports', data),
  delete: (id) => api.delete(`/api/team-reports/${id}`),
};

// Productivity
export const productivityApi = {
  teamWork: (projectId) => api.get('/api/productivity/team-work', { params: { project_id: projectId } }),
  manpowerOutput: (projectId) => api.get('/api/productivity/manpower-output', { params: { project_id: projectId } }),
  materialUsage: (projectId) => api.get('/api/productivity/material-usage', { params: { project_id: projectId } }),
};

// ==================== PHASE C APIs ====================

export const budgetApi = {
  list: (projectId) => api.get('/api/budgets', { params: { project_id: projectId } }),
  create: (data) => api.post('/api/budgets', data),
  update: (id, data) => api.put(`/api/budgets/${id}`, data),
  delete: (id) => api.delete(`/api/budgets/${id}`),
};

export const costApi = {
  list: (params) => api.get('/api/costs', { params }),
  derive: (projectId) => api.get(`/api/costs/derive?project_id=${projectId}`),
  createExpense: (data) => api.post('/api/costs/expense', data),
  delete: (id) => api.delete(`/api/costs/${id}`),
};

export const journalApi = {
  listEntries: (projectId) => api.get('/api/journal/entries', { params: { project_id: projectId } }),
  getLines: (entryId) => api.get(`/api/journal/entries/${entryId}/lines`),
  trialBalance: (projectId) => api.get('/api/journal/trial-balance', { params: { project_id: projectId } }),
};

export const invoiceApi = {
  list: (projectId) => api.get('/api/invoices', { params: { project_id: projectId } }),
  create: (data) => api.post('/api/invoices', data),
  delete: (id) => api.delete(`/api/invoices/${id}`),
  listAttachments: (invoiceId) => api.get(`/api/invoices/${invoiceId}/attachments`),
  uploadAttachment: (invoiceId, formData) => api.post(`/api/invoices/${invoiceId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAttachment: (invoiceId, fileId) => api.delete(`/api/invoices/${invoiceId}/attachments/${fileId}`),
  getFileUrl: (filename) => `${API_URL}/api/invoices/attachments/file/${filename}`,
};

export const paymentApi = {
  list: (invoiceId) => api.get('/api/payments', { params: { invoice_id: invoiceId } }),
  create: (data) => api.post('/api/payments', data),
};

export const variationApi = {
  list: (params) => api.get('/api/variations', { params }),
  create: (data) => api.post('/api/variations', data),
  approve: (id) => api.put(`/api/variations/${id}/approve`),
  execute: (id) => api.put(`/api/variations/${id}/execute`),
  delete: (id) => api.delete(`/api/variations/${id}`),
};

export const financialApi = {
  summary: (projectId) => api.get('/api/financial/summary', { params: { project_id: projectId } }),
  budgetVsActual: (projectId) => api.get('/api/financial/budget-vs-actual', { params: { project_id: projectId } }),
};

// ==================== PHASE D APIs ====================

export const documentApi = {
  list: (params) => api.get('/api/documents', { params }),
  get: (id) => api.get(`/api/documents/${id}`),
  create: (formData) => api.post('/api/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/api/documents/${id}`, data),
  delete: (id) => api.delete(`/api/documents/${id}`),
  changeStatus: (id, data) => api.put(`/api/documents/${id}/status`, data),
  getVersions: (id) => api.get(`/api/documents/${id}/versions`),
  addVersion: (id, formData) => api.post(`/api/documents/${id}/versions`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const approvalApi = {
  list: (params) => api.get('/api/approvals', { params }),
  review: (id, data) => api.put(`/api/approvals/${id}`, data),
};

export const auditApi = {
  logs: (params) => api.get('/api/audit/logs', { params }),
  loginHistory: (limit) => api.get('/api/audit/login-history', { params: { limit } }),
};

export const notificationApi = {
  list: (unreadOnly) => api.get('/api/notifications', { params: { unread_only: unreadOnly } }),
  unreadCount: () => api.get('/api/notifications/unread-count'),
  markRead: (id) => api.put(`/api/notifications/${id}/read`),
  markAllRead: () => api.put('/api/notifications/read-all'),
  generate: () => api.post('/api/notifications/generate'),
};

export const calendarApi = {
  events: (projectId) => api.get('/api/calendar/events', { params: { project_id: projectId } }),
};

export const meetingApi = {
  list: (projectId) => api.get('/api/meetings', { params: { project_id: projectId } }),
  create: (data) => api.post('/api/meetings', data),
  update: (id, data) => api.put(`/api/meetings/${id}`, data),
  delete: (id) => api.delete(`/api/meetings/${id}`),
  addAction: (id, data) => api.post(`/api/meetings/${id}/actions`, data),
  updateAction: (id, actionId, data) => api.put(`/api/meetings/${id}/actions/${actionId}`, data),
};

export const settingsApi = {
  get: () => api.get('/api/settings'),
  update: (data) => api.put('/api/settings', data),
  uploadLogo: (formData) => api.post('/api/settings/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const reportApi = {
  technical: (params) => api.get('/api/reports/technical', { params }),
  operational: (params) => api.get('/api/reports/operational', { params }),
  financial: (params) => api.get('/api/reports/financial', { params }),
  executiveDashboard: () => api.get('/api/reports/executive-dashboard'),
  projectDashboard: (projectId) => api.get('/api/reports/project-dashboard', { params: { project_id: projectId } }),
  siteDashboard: (projectId) => api.get('/api/reports/site-dashboard', { params: { project_id: projectId } }),
};

export const legalApi = {
  getPages: () => api.get('/api/legal/pages'),
  getPage: (type) => api.get(`/api/legal/pages/${type}`),
  updatePage: (type, data) => api.put(`/api/legal/pages/${type}`, data),
  getAcceptanceStatus: () => api.get('/api/legal/acceptance/status'),
  accept: () => api.post('/api/legal/acceptance/accept'),
  getAcceptanceHistory: () => api.get('/api/legal/acceptance/history'),
};

// ==================== TAX APIs ====================

export const taxApi = {
  listRates: (activeOnly) => api.get('/api/tax/config/rates', { params: { active_only: activeOnly } }),
  createRate: (data) => api.post('/api/tax/config/rates', data),
  updateRate: (id, data) => api.put(`/api/tax/config/rates/${id}`, data),
  deleteRate: (id) => api.delete(`/api/tax/config/rates/${id}`),
  getTRN: () => api.get('/api/tax/config/trn'),
  updateTRN: (trn) => api.put('/api/tax/config/trn', { trn }),
  ledger: (params) => api.get('/api/tax/ledger', { params }),
  ledgerSummary: (projectId) => api.get('/api/tax/ledger/summary', { params: { project_id: projectId } }),
  vatReport: (params) => api.get('/api/tax/reports/vat', { params }),
};

// ==================== SUPER ADMIN APIs ====================

export const superAdminApi = {
  listTenants: () => api.get('/api/superadmin/tenants'),
  getTenant: (id) => api.get(`/api/superadmin/tenants/${id}`),
  createTenant: (data) => api.post('/api/superadmin/tenants', data),
  updateTenant: (id, data) => api.put(`/api/superadmin/tenants/${id}`, data),
  suspendTenant: (id) => api.delete(`/api/superadmin/tenants/${id}`),
  listUsers: (tenantId) => api.get('/api/superadmin/users', { params: { tenant_id: tenantId } }),
  updateUser: (id, data) => api.put(`/api/superadmin/users/${id}`, data),
  resetPassword: (id, password) => api.post(`/api/superadmin/users/${id}/reset-password`, { new_password: password }),
  disableUser: (id) => api.post(`/api/superadmin/users/${id}/disable`),
  enableUser: (id) => api.post(`/api/superadmin/users/${id}/enable`),
  generateOtp: (id) => api.post(`/api/superadmin/users/${id}/generate-otp`),
  auditLog: (limit) => api.get('/api/superadmin/audit', { params: { limit } }),
  listBackups: () => api.get('/api/superadmin/backups'),
  createBackup: () => api.post('/api/superadmin/backups/create'),
  deleteBackup: (name) => api.delete(`/api/superadmin/backups/${name}`),
  restoreBackup: (backupId, confirm) => api.post('/api/superadmin/backups/restore', { backup_id: backupId, confirm }),
  restoreLogs: () => api.get('/api/superadmin/backups/restore-logs'),
};

// ==================== SECURITY QUESTIONS APIs ====================

export const securityApi = {
  getQuestions: () => api.get('/api/auth/security-questions'),
  setQuestion: (data) => api.post('/api/auth/set-security-question', data),
};

// ==================== PHASE E APIs ====================

export const employeeApi = {
  list: (params) => api.get('/api/employees', { params }),
  create: (data) => api.post('/api/employees', data),
  update: (id, data) => api.put(`/api/employees/${id}`, data),
  delete: (id) => api.delete(`/api/employees/${id}`),
};

export const payrollApi = {
  listRuns: () => api.get('/api/payroll/runs'),
  createRun: (data) => api.post('/api/payroll/runs', data),
  getPayslips: (runId) => api.get(`/api/payroll/runs/${runId}/payslips`),
  adjustPayslip: (id, data) => api.put(`/api/payroll/payslips/${id}`, data),
  processRun: (runId) => api.put(`/api/payroll/runs/${runId}/process`),
  deleteRun: (runId) => api.delete(`/api/payroll/runs/${runId}`),
};

export const supplierApi = {
  list: (params) => api.get('/api/suppliers', { params }),
  create: (data) => api.post('/api/suppliers', data),
  update: (id, data) => api.put(`/api/suppliers/${id}`, data),
  delete: (id) => api.delete(`/api/suppliers/${id}`),
};

export const inventoryApi = {
  listWarehouses: (projectId) => api.get('/api/inventory/warehouses', { params: { project_id: projectId } }),
  createWarehouse: (data) => api.post('/api/inventory/warehouses', data),
  deleteWarehouse: (id) => api.delete(`/api/inventory/warehouses/${id}`),
  getStock: (params) => api.get('/api/inventory/stock', { params }),
  listMovements: (params) => api.get('/api/inventory/movements', { params }),
  createMovement: (data) => api.post('/api/inventory/movements', data),
};

export const purchaseOrderApi = {
  list: (params) => api.get('/api/purchase-orders', { params }),
  create: (data) => api.post('/api/purchase-orders', data),
  approve: (id) => api.put(`/api/purchase-orders/${id}/approve`),
  receive: (id) => api.put(`/api/purchase-orders/${id}/receive`),
  delete: (id) => api.delete(`/api/purchase-orders/${id}`),
};

export const accountingApi = {
  listAccounts: () => api.get('/api/accounting/chart-of-accounts'),
  createAccount: (data) => api.post('/api/accounting/chart-of-accounts', data),
  updateAccount: (id, data) => api.put(`/api/accounting/chart-of-accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/api/accounting/chart-of-accounts/${id}`),
  seedCoa: () => api.post('/api/accounting/seed-coa'),
  generalLedger: (params) => api.get('/api/accounting/general-ledger', { params }),
  trialBalance: (params) => api.get('/api/accounting/trial-balance', { params }),
  profitLoss: (params) => api.get('/api/accounting/profit-loss', { params }),
  balanceSheet: (params) => api.get('/api/accounting/balance-sheet', { params }),
};

export const advReportsApi = {
  projectProfitability: () => api.get('/api/advanced-reports/project-profitability'),
  resourceUtilization: (params) => api.get('/api/advanced-reports/resource-utilization', { params }),
  payrollSummary: () => api.get('/api/advanced-reports/payroll-summary'),
  inventoryValuation: () => api.get('/api/advanced-reports/inventory-valuation'),
};

export const rbacApi = {
  getModules: () => api.get('/api/rbac/modules'),
  getRoles: () => api.get('/api/rbac/roles'),
  getPermissions: () => api.get('/api/rbac/permissions'),
  getMyPermissions: () => api.get('/api/rbac/my-permissions'),
  updatePermissions: (data) => api.put('/api/rbac/permissions', data),
  resetDefaults: () => api.post('/api/rbac/reset-defaults'),
};

export const inviteApi = {
  list: () => api.get('/api/invites'),
  create: (data) => api.post('/api/invites', data),
  revoke: (id) => api.delete(`/api/invites/${id}`),
  resend: (id) => api.post(`/api/invites/${id}/resend`),
  validate: (token) => api.get(`/api/auth/invite/${token}`),
  accept: (data) => api.post('/api/auth/accept-invite', data),
};

export const demoLeadApi = {
  list: () => api.get('/api/demo-leads'),
};

// ==================== SUBMITTALS APIs ====================

export const submittalApi = {
  list: (params) => api.get('/api/submittals', { params }),
  get: (id) => api.get(`/api/submittals/${id}`),
  create: (formData) => api.post('/api/submittals', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/api/submittals/${id}`, data),
  delete: (id) => api.delete(`/api/submittals/${id}`),
  changeStatus: (id, data) => api.put(`/api/submittals/${id}/status`, data),
  updateProcurement: (id, data) => api.put(`/api/submittals/${id}/procurement`, data),
  getComments: (id) => api.get(`/api/submittals/${id}/comments`),
  addComment: (id, data) => api.post(`/api/submittals/${id}/comments`, data),
  getRevisions: (id) => api.get(`/api/submittals/${id}/revisions`),
  addRevision: (id, formData) => api.post(`/api/submittals/${id}/revisions`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  summary: (params) => api.get('/api/submittals/summary', { params }),
  types: () => api.get('/api/submittals/types'),
  // Templates
  listTemplates: () => api.get('/api/submittals/templates/list'),
  createTemplate: (formData) => api.post('/api/submittals/templates', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateTemplate: (id, formData) => api.put(`/api/submittals/templates/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteTemplate: (id) => api.delete(`/api/submittals/templates/${id}`),
};

// ==================== PLANNING APIs ====================

export const planningApi = {
  // Activities
  listActivities: (params) => api.get('/api/planning/activities', { params }),
  getActivity: (id) => api.get(`/api/planning/activities/${id}`),
  createActivity: (data) => api.post('/api/planning/activities', data),
  updateActivity: (id, data) => api.put(`/api/planning/activities/${id}`, data),
  patchActivity: (id, data) => api.patch(`/api/planning/activities/${id}`, data),
  deleteActivity: (id) => api.delete(`/api/planning/activities/${id}`),
  approveActivity: (id, data) => api.put(`/api/planning/activities/${id}/approval`, data),
  setDependencies: (id, data) => api.put(`/api/planning/activities/${id}/dependencies`, data),
  importCSV: (formData) => api.post('/api/planning/activities/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Categories
  listCategories: () => api.get('/api/planning/categories'),
  createCategory: (data) => api.post('/api/planning/categories', data),
  updateCategory: (id, data) => api.put(`/api/planning/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/api/planning/categories/${id}`),
  // Meta & Summary
  getMeta: () => api.get('/api/planning/meta'),
  getSummary: (params) => api.get('/api/planning/summary', { params }),
};

// ==================== AI APIs ====================

export const aiApi = {
  // Settings
  getSettings: () => api.get('/api/ai/settings'),
  updateSettings: (data) => api.put('/api/ai/settings', data),
  // Planning Assistant
  generateSchedule: (data) => api.post('/api/ai/planning/generate', data),
  riskAnalysis: (data) => api.post('/api/ai/planning/risk-analysis', data),
  categorize: (data) => api.post('/api/ai/planning/categorize', data),
  // Document Review
  reviewSubmittal: (data) => api.post('/api/ai/submittals/review', data),
  // Chat - uses fetch for SSE
  chatStream: (data) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return fetch(`${baseUrl}/api/ai/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
  },
  getChatHistory: (sessionId) => api.get('/api/ai/chat/history', { params: { session_id: sessionId } }),
  clearChatHistory: (sessionId) => api.delete('/api/ai/chat/history', { params: { session_id: sessionId } }),
  // Universal Search
  search: (q) => api.get('/api/ai/search', { params: { q } }),
};

export default api;
